import { useEffect, useState } from "react";
import { BarChart3, CalendarDays, Clock3, LayoutDashboard, LogOut, Menu, Settings, UsersRound, X } from "lucide-react";
import { Attendance } from "./components/Attendance";
import { BrandMark } from "./components/BrandMark";
import { ChangePin } from "./components/ChangePin";
import { Login } from "./components/Login";
import { ManagerSurface } from "./components/Manager";
import { EmployeeLeave } from "./components/EmployeeLeave";
import { getSession, logout, type SessionUser } from "./lib/api";
import type { Screen } from "./types";

const managerNav: { screen: Screen; label: string; icon: typeof LayoutDashboard }[] = [
  { screen: "overview", label: "ภาพรวมวันนี้", icon: LayoutDashboard },
  { screen: "employees", label: "พนักงาน", icon: UsersRound },
  { screen: "leave", label: "การลา", icon: CalendarDays },
  { screen: "reports", label: "รายงาน", icon: BarChart3 },
  { screen: "settings", label: "ตั้งค่า", icon: Settings }
];

export default function App() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [screen, setScreen] = useState<Screen>("overview");
  const [mobileMenu, setMobileMenu] = useState(false);
  const [employeeScreen, setEmployeeScreen] = useState<"attendance" | "leave">("attendance");

  useEffect(() => {
    getSession().then(setUser).finally(() => setLoading(false));
  }, []);

  const signOut = async () => {
    await logout();
    setUser(null);
    setScreen("overview");
  };

  if (loading) return <main className="app-loading"><BrandMark /><p>กำลังเปิดระบบ…</p></main>;
  if (!user) return <Login onSuccess={setUser} />;
  if (user.mustChangePin) return <ChangePin user={user} onComplete={() => setUser({ ...user, mustChangePin: false })} />;

  if (user.role === "employee") {
    return (
      <div className="employee-shell">
        <a className="skip-link" href="#main-content">ข้ามไปเนื้อหาหลัก</a>
        <div className="employee-topbar"><BrandMark /><span className="signed-in-code">{user.code}</span><button className="icon-button logout-button" aria-label="ออกจากระบบ" onClick={() => void signOut()}><LogOut size={20} /></button></div>
        {employeeScreen === "attendance" ? <Attendance user={user} /> : <EmployeeLeave />}
        <nav className="employee-bottom-nav employee-live-nav" aria-label="เมนูพนักงาน">
          <button className={employeeScreen === "attendance" ? "is-active" : ""} onClick={() => setEmployeeScreen("attendance")}><Clock3 size={21} /><span>ลงเวลา</span></button>
          <button className={employeeScreen === "leave" ? "is-active" : ""} onClick={() => setEmployeeScreen("leave")}><CalendarDays size={21} /><span>ขอลา</span></button>
        </nav>
      </div>
    );
  }

  return (
    <div className="manager-shell">
      <a className="skip-link" href="#main-content">ข้ามไปเนื้อหาหลัก</a>
      <header className="manager-topbar">
        <button className="menu-button" onClick={() => setMobileMenu(true)} aria-label="เปิดเมนู"><Menu size={22} /></button>
        <div className="mobile-brand"><BrandMark /></div>
        <div className="branch-switch"><span>ระบบลงเวลาร้านอาหาร</span><small>หลังบ้านผู้จัดการ</small></div>
        <button className="manager-profile" onClick={() => void signOut()}><span className="profile-avatar">{user.fullName.slice(0, 1)}</span><span><strong>{user.fullName}</strong><small>{user.position}</small></span><LogOut size={17} /></button>
      </header>
      <aside className={`manager-sidebar ${mobileMenu ? "is-open" : ""}`}>
        <div className="sidebar-brand"><BrandMark /><button className="icon-button close-menu" onClick={() => setMobileMenu(false)} aria-label="ปิดเมนู"><X size={21} /></button></div>
        <nav aria-label="เมนูผู้จัดการ">{managerNav.map(({ screen: itemScreen, label, icon: Icon }) => <button key={itemScreen} className={screen === itemScreen ? "is-active" : ""} onClick={() => { setScreen(itemScreen); setMobileMenu(false); }}><Icon size={19} /><span>{label}</span></button>)}</nav>
        <div className="sidebar-footer"><button onClick={() => void signOut()}><LogOut size={19} /><span>ออกจากระบบ</span></button><p>เข้างาน v1.0</p></div>
      </aside>
      {mobileMenu && <button className="sidebar-backdrop" onClick={() => setMobileMenu(false)} aria-label="ปิดเมนู" />}
      <ManagerSurface screen={screen} />
      <nav className="manager-bottom-nav" aria-label="เมนูผู้จัดการบนมือถือ">{managerNav.slice(0, 5).map(({ screen: itemScreen, label, icon: Icon }) => <button key={itemScreen} className={screen === itemScreen ? "is-active" : ""} onClick={() => setScreen(itemScreen)}><Icon size={20} /><span>{label === "ภาพรวมวันนี้" ? "ภาพรวม" : label}</span></button>)}</nav>
    </div>
  );
}
